import React, { useState } from 'react';
import { Phone, Mail, MapPin, Clock, Send } from 'lucide-react';
import Card from '../components/ui/Card';
import Button from '../components/ui/Button';
import { BUSINESS_INFO, OPENING_HOURS } from '../utils/constants';
import { generateWhatsAppMessage, generateEmailLink } from '../utils/helpers';
import WhatsAppIcon from '../components/ui/WhatsAppIcon';

const Contact = () => {
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    email: '',
    postcode: '',
    service: '',
    date: '',
    time: '',
    notes: '',
  });

  const [errors, setErrors] = useState({});

  const handleChange = (field, value) => {
    setFormData({ ...formData, [field]: value });
    if (errors[field]) setErrors({ ...errors, [field]: '' });
  };

  const validateForm = () => {
    const newErrors = {};
    if (!formData.name.trim()) newErrors.name = 'Name is required';
    if (!formData.phone.trim()) {
      newErrors.phone = 'Phone number is required';
    } else if (!/^[\d\s+()-]+$/.test(formData.phone)) {
      newErrors.phone = 'Please enter a valid phone number';
    }
    if (!formData.postcode.trim()) newErrors.postcode = 'Location/Postcode is required';
    if (!formData.service) newErrors.service = 'Please select a service';
    if (!formData.date) newErrors.date = 'Preferred date is required';
    if (!formData.time) newErrors.time = 'Preferred time is required';
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleWhatsAppSubmit = () => {
    if (validateForm()) {
      const message = generateWhatsAppMessage(formData);
      window.open(`${BUSINESS_INFO.whatsappLink}?text=${message}`, '_blank');
      setFormData({ name: '', phone: '', email: '', postcode: '', service: '', date: '', time: '', notes: '' });
    }
  };

  const handleEmailSubmit = () => {
    if (validateForm()) {
      window.location.href = generateEmailLink(formData);
      setFormData({ name: '', phone: '', email: '', postcode: '', service: '', date: '', time: '', notes: '' });
    }
  };

  return (
    <div>
      <section className="page-hero">
        <div className="container">
          <div className="page-hero-content">
            <div className="eyebrow">Contact</div>
            <h1>Get In Touch</h1>
            <p>
              Book your service or reach out with any questions. We're here to help.
            </p>
          </div>
        </div>
      </section>

      <section className="section section-dark">
        <div className="container">
          <div className="contact-grid">
            {/* Left: Contact Info */}
            <div>
              <h2 style={{ marginBottom: '1.5rem', color: 'var(--white)' }}>
                Contact Information
              </h2>

              <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                <Card hover className="contact-info-card">
                  <div className="contact-info-icon">
                    <Phone size={22} />
                  </div>
                  <div className="contact-info-content">
                    <h3>Phone / WhatsApp</h3>
                    <a
                      href={BUSINESS_INFO.whatsappLink}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="contact-info-link"
                    >
                      {BUSINESS_INFO.phoneDisplay}
                    </a>
                    <p className="contact-info-text">Click to chat on WhatsApp instantly</p>
                  </div>
                </Card>

                <Card hover className="contact-info-card">
                  <div className="contact-info-icon">
                    <Mail size={22} />
                  </div>
                  <div className="contact-info-content">
                    <h3>Email</h3>
                    <a href={`mailto:${BUSINESS_INFO.email}`} className="contact-info-link">
                      {BUSINESS_INFO.email}
                    </a>
                    <p className="contact-info-text">Send us an email for quotes or inquiries</p>
                  </div>
                </Card>

                <Card hover className="contact-info-card">
                  <div className="contact-info-icon">
                    <MapPin size={22} />
                  </div>
                  <div className="contact-info-content">
                    <h3>Service Area</h3>
                    <p style={{ color: 'var(--white)', marginBottom: '0.25rem', fontWeight: 600 }}>
                      {BUSINESS_INFO.address}
                    </p>
                    <p className="contact-info-text">
                      We provide mobile service across Sheffield and surrounding areas
                    </p>
                  </div>
                </Card>

                <Card hover className="contact-info-card">
                  <div className="contact-info-icon">
                    <Clock size={22} />
                  </div>
                  <div className="contact-info-content">
                    <h3>Opening Hours</h3>
                    <div className="contact-hours">
                      <div className="contact-hour-row">
                        <span>{OPENING_HOURS.weekday.days}</span>
                        <span>{OPENING_HOURS.weekday.hours}</span>
                      </div>
                      <div className="contact-hour-row">
                        <span>{OPENING_HOURS.weekend.days}</span>
                        <span>{OPENING_HOURS.weekend.hours}</span>
                      </div>
                    </div>
                  </div>
                </Card>

                <Card
                  style={{
                    padding: '1.5rem',
                    background: 'linear-gradient(135deg, rgba(37, 211, 102, 0.08) 0%, rgba(255, 215, 0, 0.05) 100%)',
                    border: '1px solid rgba(37, 211, 102, 0.3)',
                  }}
                >
                  <h3 style={{ marginBottom: '0.5rem', color: 'var(--white)' }}>
                    Prefer Quick Messaging?
                  </h3>
                  <p style={{ marginBottom: '1rem', color: 'var(--gray)', fontSize: '0.9375rem' }}>
                    Chat with us directly on WhatsApp for instant responses and easy booking.
                  </p>
                  <a href={BUSINESS_INFO.whatsappLink} target="_blank" rel="noopener noreferrer">
                    <Button variant="whatsapp" className="w-full" icon={WhatsAppIcon}>
                      Open WhatsApp Chat
                    </Button>
                  </a>
                </Card>
              </div>
            </div>

            {/* Right: Booking Form */}
            <div>
              <h2 style={{ marginBottom: '1.5rem', color: 'var(--white)' }}>
                Book Your Service
              </h2>
              <Card style={{ padding: '2rem' }}>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem' }}>
                  <div className="form-group">
                    <label className="form-label">
                      Full Name <span className="required">*</span>
                    </label>
                    <input
                      type="text"
                      value={formData.name}
                      onChange={(e) => handleChange('name', e.target.value)}
                      className={`form-input ${errors.name ? 'error' : ''}`}
                      placeholder="Your Full Name"
                    />
                    {errors.name && <div className="form-error">{errors.name}</div>}
                  </div>

                  <div className="form-group">
                    <label className="form-label">
                      Phone Number <span className="required">*</span>
                    </label>
                    <input
                      type="tel"
                      value={formData.phone}
                      onChange={(e) => handleChange('phone', e.target.value)}
                      className={`form-input ${errors.phone ? 'error' : ''}`}
                      placeholder="07XXX XXXXXX"
                    />
                    {errors.phone && <div className="form-error">{errors.phone}</div>}
                  </div>

                  <div className="form-group">
                    <label className="form-label">Email Address (Optional)</label>
                    <input
                      type="email"
                      value={formData.email}
                      onChange={(e) => handleChange('email', e.target.value)}
                      className="form-input"
                      placeholder="john@example.com"
                    />
                  </div>

                  <div className="form-group">
                    <label className="form-label">
                      Postcode / Location <span className="required">*</span>
                    </label>
                    <input
                      type="text"
                      value={formData.postcode}
                      onChange={(e) => handleChange('postcode', e.target.value)}
                      className={`form-input ${errors.postcode ? 'error' : ''}`}
                      placeholder="S1 1AA or Home Address"
                    />
                    {errors.postcode && <div className="form-error">{errors.postcode}</div>}
                  </div>

                  <div className="form-group">
                    <label className="form-label">
                      Service Required <span className="required">*</span>
                    </label>
                    <select
                      value={formData.service}
                      onChange={(e) => handleChange('service', e.target.value)}
                      className={`form-select ${errors.service ? 'error' : ''}`}
                    >
                      <option value="">Select a service...</option>
                      <option value="Bronze Package">Bronze Package</option>
                      <option value="Silver Package">Silver Package</option>
                      <option value="Gold Package">Gold Package</option>
                      <option value="Platinum Package">Platinum Package</option>
                      <option value="Custom Service">Custom Service / Multiple Vehicles</option>
                      <option value="Other">Other (Please specify in notes)</option>
                    </select>
                    {errors.service && <div className="form-error">{errors.service}</div>}
                  </div>

                  <div className="grid grid-2" style={{ gap: '1rem' }}>
                    <div className="form-group">
                      <label className="form-label">
                        Preferred Date <span className="required">*</span>
                      </label>
                      <input
                        type="date"
                        value={formData.date}
                        onChange={(e) => handleChange('date', e.target.value)}
                        min={new Date().toISOString().split('T')[0]}
                        className={`form-input ${errors.date ? 'error' : ''}`}
                      />
                      {errors.date && <div className="form-error">{errors.date}</div>}
                    </div>
                    <div className="form-group">
                      <label className="form-label">
                        Preferred Time <span className="required">*</span>
                      </label>
                      <input
                        type="time"
                        value={formData.time}
                        onChange={(e) => handleChange('time', e.target.value)}
                        className={`form-input ${errors.time ? 'error' : ''}`}
                      />
                      {errors.time && <div className="form-error">{errors.time}</div>}
                    </div>
                  </div>

                  <div className="form-group">
                    <label className="form-label">Additional Notes (Optional)</label>
                    <textarea
                      value={formData.notes}
                      onChange={(e) => handleChange('notes', e.target.value)}
                      className="form-textarea"
                      placeholder="Any special requirements or additional services needed?"
                    />
                  </div>

                  <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem', marginTop: '0.5rem' }}>
                    <Button
                      variant="whatsapp"
                      size="lg"
                      className="w-full"
                      icon={WhatsAppIcon}
                      onClick={handleWhatsAppSubmit}
                    >
                      Book via WhatsApp
                    </Button>

                    <Button
                      variant="outline"
                      size="lg"
                      className="w-full"
                      icon={Send}
                      onClick={handleEmailSubmit}
                    >
                      Send via Email
                    </Button>
                  </div>

                  <p style={{ fontSize: '0.8125rem', color: 'var(--gray-dark)', textAlign: 'center', marginTop: '1rem' }}>
                    Choose your preferred contact method. Both will send your booking details.
                  </p>
                </div>
              </Card>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Contact;